<script setup lang="ts">
/**
 * The bid project as the folder tree the team actually works in.
 *
 * A bid lives in a folder structure (A] Udbudsmateriale … F] Evaluering)
 * long before it is a conversation, and the person here is assembling that
 * structure to copy into the `#UID-` folder on SharePoint. So uploads and
 * generated documents are shown as ONE tree: an uploaded A] bilag and a
 * generated C] kravmatrix are the same kind of thing to them.
 *
 * Empty folders are shown, greyed. The shape of a bid is part of the
 * information — seeing that B] and C] are still empty is the point.
 */
import { computed, ref, watch } from "vue";

import { acquireToken, USE_REAL_AUTH } from "../auth/msal";

const props = defineProps<{
  conversationId: number | null;
  devUser: string;
  /** Bumped by the parent after an upload or a completed turn. */
  refreshKey?: number;
}>();

type Entry = {
  origin: "upload" | "generated";
  id: string;
  name: string;
  rel_path?: string;
  role?: string;
  kind?: string;
  format?: string;
  byte_size?: number;
  index_status?: string;
  download_url?: string;
};
type Folder = { name: string; files: Entry[] };

const folders = ref<Folder[]>([]);
const unfiled = ref<Entry[]>([]);
const generatedCount = ref(0);
const project = ref<Record<string, string>>({});
const loading = ref(false);
const zipping = ref(false);
const error = ref<string | null>(null);

const apiBase = import.meta.env.VITE_API_BASE ?? "";

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: "application/json" };
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    if (t) h["Authorization"] = `Bearer ${t.token}`;
  } else if (props.devUser) {
    h["X-Dev-User"] = props.devUser;
  }
  return h;
}

async function load() {
  if (props.conversationId === null) {
    folders.value = [];
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const r = await fetch(
      `${apiBase}/api/conversations/${props.conversationId}/project`,
      { headers: await authHeaders() },
    );
    if (!r.ok) throw new Error(`kunne ikke hente projektet (${r.status})`);
    const data = await r.json();
    folders.value = data.folders ?? [];
    unfiled.value = data.unfiled ?? [];
    generatedCount.value = data.generated_count ?? 0;
    project.value = data.project ?? {};
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

/**
 * The zip needs the bearer, so it cannot be a plain <a href>. Fetch it,
 * hand the browser a blob URL, and revoke it once the click is done.
 */
async function downloadZip() {
  if (props.conversationId === null) return;
  zipping.value = true;
  error.value = null;
  try {
    const r = await fetch(
      `${apiBase}/api/conversations/${props.conversationId}/project.zip`,
      { headers: await authHeaders() },
    );
    if (!r.ok) throw new Error(`download fejlede (${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.value.uid || "bid"}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    zipping.value = false;
  }
}

async function downloadOne(entry: Entry) {
  if (!entry.download_url) return;
  try {
    const r = await fetch(`${apiBase}${entry.download_url}`, {
      headers: await authHeaders(),
    });
    if (!r.ok) throw new Error(`download fejlede (${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entry.name}.${entry.format ?? "docx"}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}

watch(
  () => [props.conversationId, props.refreshKey],
  () => void load(),
  { immediate: true },
);

const hasAnything = computed(
  () => folders.value.some((f) => f.files.length > 0) || unfiled.value.length > 0,
);
const heading = computed(() => {
  const uid = project.value.uid;
  const title = project.value.title;
  if (uid && title) return `#UID-${uid} — ${title}`;
  if (uid) return `#UID-${uid}`;
  return "Bid-projekt";
});
</script>

<template>
  <div v-if="hasAnything || loading" class="bid-project">
    <header class="bp-head">
      <span class="bp-title">{{ heading }}</span>
      <span v-if="project.deadline" class="bp-deadline">
        Tidsfrist {{ project.deadline }}
      </span>
      <button
        v-if="generatedCount > 0"
        class="bp-zip"
        :disabled="zipping"
        @click="downloadZip"
      >
        {{ zipping ? "Pakker…" : "Download genererede filer (.zip)" }}
      </button>
    </header>

    <p v-if="error" class="bp-error">{{ error }}</p>
    <p v-else-if="generatedCount === 0" class="bp-hint">
      Endnu ingen genererede filer. Bed assistenten om fx en kravmatrix —
      den lægges i C] Arbejdsmappe og kan hentes herfra.
    </p>
    <p v-else class="bp-hint">
      Zip-filen indeholder de {{ generatedCount }} genererede
      {{ generatedCount === 1 ? "fil" : "filer" }} i mappestrukturen.
      A] Udbudsmateriale er ikke med — det er jeres eget materiale, som I
      allerede har i original.
    </p>

    <ul class="bp-folders">
      <li
        v-for="f in folders"
        :key="f.name"
        :class="['bp-folder', { 'is-empty': f.files.length === 0 }]"
      >
        <div class="bp-folder-name">
          {{ f.name }}
          <span class="bp-count">{{ f.files.length || "" }}</span>
        </div>
        <ul v-if="f.files.length" class="bp-files">
          <li v-for="entry in f.files" :key="entry.id" class="bp-file">
            <span class="bp-file-name">{{ entry.name }}</span>
            <span v-if="entry.origin === 'generated'" class="bp-badge">
              {{ entry.format }}
            </span>
            <span
              v-else-if="entry.index_status && entry.index_status !== 'ready'"
              class="bp-badge bp-badge--muted"
            >
              {{ entry.index_status }}
            </span>
            <button
              v-if="entry.origin === 'generated'"
              class="bp-dl"
              title="Download"
              @click="downloadOne(entry)"
            >
              ↓
            </button>
          </li>
        </ul>
      </li>
    </ul>

    <ul v-if="unfiled.length" class="bp-files bp-unfiled">
      <li v-for="entry in unfiled" :key="entry.id" class="bp-file">
        <span class="bp-file-name">{{ entry.name }}</span>
        <button
          v-if="entry.origin === 'generated'"
          class="bp-dl"
          @click="downloadOne(entry)"
        >
          ↓
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.bid-project {
  border: 1px solid var(--border, #e2e2e2);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}
.bp-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}
.bp-title { font-weight: 600; }
.bp-deadline { color: var(--muted, #666); font-size: 0.8rem; }
.bp-zip {
  margin-left: auto;
  cursor: pointer;
  border: 1px solid var(--border, #d0d0d0);
  border-radius: 6px;
  padding: 0.25rem 0.6rem;
  background: transparent;
  font-size: 0.8rem;
}
.bp-zip:disabled { opacity: 0.6; cursor: default; }
.bp-error { color: #b00020; margin: 0.25rem 0; }
.bp-hint { color: var(--muted, #777); margin: 0 0 0.4rem; font-size: 0.78rem; }
.bp-folders { list-style: none; margin: 0; padding: 0; }
.bp-folder { margin-bottom: 0.3rem; }
.bp-folder-name { font-weight: 500; }
.bp-folder.is-empty .bp-folder-name { color: var(--muted, #999); font-weight: 400; }
.bp-count { color: var(--muted, #888); font-weight: 400; margin-left: 0.3rem; }
.bp-files { list-style: none; margin: 0.15rem 0 0 1rem; padding: 0; }
.bp-file { display: flex; align-items: center; gap: 0.4rem; padding: 0.1rem 0; }
.bp-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bp-badge {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: var(--muted, #666);
  border: 1px solid var(--border, #ddd);
  border-radius: 4px;
  padding: 0 0.25rem;
}
.bp-badge--muted { opacity: 0.7; }
.bp-dl {
  margin-left: auto;
  cursor: pointer;
  border: none;
  background: transparent;
  font-size: 0.9rem;
}
</style>
